/**
 * toast.jsx — Toast notification system v2.0
 *
 * Uso:
 *   1. Envolver o app com <ToastProvider> em SafeZapApp.jsx
 *   2. Em qualquer componente: const { showToast } = useToast()
 *   3. showToast({ title, message, variant, duration })
 *      variant: "success" | "warning" | "danger" | "info"
 *
 * Sem dependências externas — só Context + CSS já adicionado em styles.css
 */
import React, { createContext, useCallback, useContext, useRef, useState, useEffect } from "react";
import { Icon } from "./components";

const ToastContext = createContext({ showToast: () => {}, dismiss: () => {} });

let _idSeq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((arr) => arr.filter((t) => t.id !== id));
    const tm = timersRef.current.get(id);
    if (tm) { clearTimeout(tm); timersRef.current.delete(id); }
  }, []);

  const showToast = useCallback(({ title, message, variant = "info", duration = 4000 } = {}) => {
    const id = ++_idSeq;
    setToasts((arr) => [...arr, { id, title, message, variant }]);
    if (duration > 0) {
      const tm = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, tm);
    }
    return id;
  }, [dismiss]);

  // cleanup on unmount
  useEffect(() => () => {
    timersRef.current.forEach((tm) => clearTimeout(tm));
    timersRef.current.clear();
  }, []);

  const iconFor = (v) =>
    v === "success" ? "check"
    : v === "warning" ? "warning"
    : v === "danger" ? "warning"
    : "info";

  return (
    <ToastContext.Provider value={{ showToast, dismiss }}>
      {children}
      {toasts.length > 0 && (
        <div className="toast-container" role="region" aria-live="polite" aria-label="Notificações">
          {toasts.map((t) => (
            <div key={t.id} className={`toast toast-${t.variant}`} role="status">
              <span className="toast-icon"><Icon name={iconFor(t.variant)} size={14} /></span>
              <div className="toast-body">
                {t.title && <div className="toast-title">{t.title}</div>}
                {t.message && <div className="toast-message">{t.message}</div>}
              </div>
              <button
                className="toast-close"
                onClick={() => dismiss(t.id)}
                aria-label="Fechar notificação"
                title="Fechar"
              >
                <Icon name="close" size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
